/**
 * QuickQuoteFormShared
 * --------------------
 * Single source of truth for the "Backorder / Hard-to-Find SKUs" Quick-Quote
 * form. Used identically in:
 *
 *   1. Public homepage (LandingPage `#quick-quote` section)
 *   2. Hospital dashboard "New Backorder" modal
 *
 * Submits to POST /api/order-request — when `hospitalContext.id` is supplied
 * the resulting backorder is tagged to that hospital and surfaces on their
 * dashboard automatically.
 *
 * Props
 *   • hospitalContext  — { id, name, contact_name, contact_email, contact_phone }
 *                        Renders the green "Submitting as <Hospital>" banner
 *                        and pre-fills contact fields when provided.
 *   • onSubmitted(resp)— Optional callback fired on a successful submission.
 *   • showWrapper      — When false (default in modal mode) skips the white
 *                        card chrome so the form blends into the modal body.
 *   • backLink         — When true and inside a hospital context, renders the
 *                        "Back to dashboard →" link on the banner.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  Camera,
  CheckCircle2,
  Send,
  Loader2,
  ShieldCheck,
  Phone,
  RefreshCw,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuickQuoteFormShared({
  hospitalContext = null,
  onSubmitted = null,
  showWrapper = true,
  backLink = true,
}) {
  const [quoteItems, setQuoteItems] = useState([{ product_name: "", sku: "", quantity: "", uom: "case", notes: "" }]);
  const [showOnTheGo, setShowOnTheGo] = useState(false);
  const [quoteImage, setQuoteImage] = useState(null);
  const [quoteImageUploading, setQuoteImageUploading] = useState(false);
  const [quoteOnTheGoNote, setQuoteOnTheGoNote] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", hospital: "" });
  const [shipping, setShipping] = useState({ city: "", state: "", zip: "" });
  const [freight, setFreight] = useState({ use_hospital_courier: false, account: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Backorder id returned by /api/order-request — used to poll for supplier
  // discovery progress so the success view can render "found from verified
  // supplier" / "found from unverified supplier" / "no immediate stock" messages.
  const [backorderId, setBackorderId] = useState(null);
  const [discovery, setDiscovery] = useState({ status: "running", summary: null, message: "", lead_count: 0 });

  // Order history (Amazon "buy it again" + typeahead) — only loads when a
  // hospital token is present in localStorage and we know the hospital id.
  const [itemHistory, setItemHistory] = useState([]);
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [activeItemIdx, setActiveItemIdx] = useState(0);

  // Prefill from Live Inventory hand-offs:
  //   • Bulk select → sessionStorage "bulk_quote_items"
  //   • Single click → URL query params prefill_name / prefill_sku / prefill_mfr
  // Runs once on mount. Bulk wins if both are present.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let filled = 0;
    try {
      const raw = sessionStorage.getItem("bulk_quote_items");
      if (raw) {
        const items = JSON.parse(raw);
        if (Array.isArray(items) && items.length > 0) {
          setQuoteItems(items.map((it) => ({
            product_name: it.product_name || "",
            sku: [it.manufacturer, it.sku].filter(Boolean).join(" · "),
            quantity: String(it.quantity || ""),
            uom: "case",
            notes: it.notes || "",
          })));
          filled = items.length;
          sessionStorage.removeItem("bulk_quote_items");
        }
      }
    } catch { /* private mode / parse fail — ignore */ }
    if (filled === 0) {
      try {
        const params = new URLSearchParams(window.location.search);
        const name = params.get("prefill_name");
        const sku = params.get("prefill_sku");
        const mfr = params.get("prefill_mfr");
        if (name || sku || mfr) {
          setQuoteItems([{
            product_name: name || "",
            sku: [mfr, sku].filter(Boolean).join(" · "),
            quantity: "",
            uom: "case",
            notes: "",
          }]);
          filled = 1;
        }
      } catch { /* ignore */ }
    }
    if (filled > 0) {
      toast.success(`${filled} item${filled === 1 ? "" : "s"} prefilled from the Live Inventory list.`);
    }
  }, []);

  const getHospitalToken = () => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("hospital_token") || null;
  };

  // Lazy-load order history once on mount when a hospital is in context.
  useEffect(() => {
    if (!hospitalContext?.id) return;
    const token = getHospitalToken();
    if (!token) return;
    fetch(`${API}/api/hospital/backorders/item-history?limit=12`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setItemHistory(d?.items || []))
      .catch(() => setItemHistory([]));
  }, [hospitalContext?.id]);

  // URL-param prefill: allow deep-links from the Live Backorder List to seed
  // the first line item. Supports ?prefill_name=&prefill_sku=&prefill_mfr=
  // and clears itself from history after applying so a refresh doesn't
  // duplicate the prefill.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const name = p.get("prefill_name");
      const sku = p.get("prefill_sku");
      const mfr = p.get("prefill_mfr");
      if (!name && !sku && !mfr) return;
      setQuoteItems((items) => {
        const next = [...items];
        next[0] = {
          ...next[0],
          product_name: name || next[0].product_name,
          sku: sku || next[0].sku,
          notes: mfr ? `Manufacturer: ${mfr}${next[0].notes ? ` · ${next[0].notes}` : ""}` : next[0].notes,
        };
        return next;
      });
      // Strip the prefill params so a refresh is idempotent, keep the hash so
      // the browser stays scrolled to the form section.
      p.delete("prefill_name"); p.delete("prefill_sku"); p.delete("prefill_mfr");
      const clean = window.location.pathname + (p.toString() ? `?${p.toString()}` : "") + window.location.hash;
      window.history.replaceState({}, "", clean);
    } catch (_) { /* no-op */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced typeahead — fires off the active item's product_name field.
  useEffect(() => {
    if (!hospitalContext?.id) return;
    const q = quoteItems[activeItemIdx]?.product_name?.trim() || "";
    if (q.length < 2) {
      setTypeaheadResults([]);
      setShowTypeahead(false);
      return;
    }
    const token = getHospitalToken();
    if (!token) return;
    const t = setTimeout(() => {
      fetch(`${API}/api/hospital/backorders/item-history?q=${encodeURIComponent(q)}&limit=6`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((d) => {
          const items = d?.items || [];
          setTypeaheadResults(items);
          setShowTypeahead(items.length > 0);
        })
        .catch(() => setTypeaheadResults([]));
    }, 220);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems[activeItemIdx]?.product_name, hospitalContext?.id, activeItemIdx]);

  const applyHistoryItem = (idx, hit) => {
    const items = [...quoteItems];
    items[idx] = {
      ...items[idx],
      product_name: hit.name || items[idx].product_name,
      sku: hit.part_number || items[idx].sku,
      quantity: items[idx].quantity || String(hit.last_quantity || ""),
      notes: items[idx].notes,
    };
    setQuoteItems(items);
    setShowTypeahead(false);
  };

  // Prefill from hospital context whenever it changes
  useEffect(() => {
    if (!hospitalContext) return;
    setContact((c) => ({
      name: c.name || hospitalContext.contact_name || "",
      email: c.email || hospitalContext.contact_email || "",
      phone: c.phone || hospitalContext.contact_phone || "",
      hospital: c.hospital || hospitalContext.name || "",
    }));
  }, [hospitalContext]);

  // Poll supplier-discovery progress while the success view is showing
  useEffect(() => {
    if (!submitted || !backorderId) return;
    let cancelled = false;
    let pollCount = 0;
    const tick = async () => {
      pollCount += 1;
      try {
        const r = await fetch(`${API}/api/public/backorders/${backorderId}/discovery-progress`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setDiscovery(data);
        if (data.status === "complete" || pollCount > 25) return; // ~75s cap
        if (!cancelled) setTimeout(tick, 3000);
      } catch (_e) {
        if (!cancelled) setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [submitted, backorderId]);

  const addItem = () => setQuoteItems([...quoteItems, { product_name: "", sku: "", quantity: "", uom: "case", notes: "" }]);
  const removeItem = (idx) => setQuoteItems(quoteItems.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => {
    const items = [...quoteItems];
    items[idx][field] = val;
    setQuoteItems(items);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large — max 10 MB"); return; }
    setQuoteImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/order-request/upload-image`, { method: "POST", body: fd });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setQuoteImage({ url: data.url, filename: data.filename, preview: URL.createObjectURL(file) });
      toast.success("Photo attached");
    } catch (_err) {
      toast.error("Couldn't upload image — try again");
    } finally {
      setQuoteImageUploading(false);
    }
  };

  const onPasteImage = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) {
          e.preventDefault();
          uploadImage(blob);
          return;
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.hospital) {
      toast.error("Please fill in your name, email, and hospital/facility name");
      return;
    }
    const validItems = quoteItems.filter((i) => i.product_name.trim());
    const hasOnTheGo = !!quoteImage?.url || !!quoteOnTheGoNote.trim();
    if (validItems.length === 0 && !hasOnTheGo) {
      toast.error("Add at least one product, paste a photo, or leave us a note");
      return;
    }
    setSubmitting(true);
    try {
      const shippingAddress = [shipping.city.trim(), shipping.state.trim(), shipping.zip.trim()].filter(Boolean).join(", ");
      const uomLabel = (u) => ({ case: "cases", each: "eaches", box: "boxes", pallet: "pallets" }[u] || u || "cases");
      const supplierNotes = [
        ...validItems.map((i) => {
          const skuPart = i.sku ? ` · SKU: ${i.sku}` : "";
          const notePart = i.notes ? ` - ${i.notes}` : "";
          return `${i.product_name}${skuPart} (Qty: ${i.quantity || 1} ${uomLabel(i.uom)})${notePart}`;
        }),
        quoteOnTheGoNote.trim() ? `[On-the-go note] ${quoteOnTheGoNote.trim()}` : "",
        quoteImage?.url ? `[Photo attached] ${quoteImage.url}` : "",
      ].filter(Boolean).join("; ");

      const itemNameStr = validItems.length > 0
        ? validItems.map((i) => (i.sku ? `${i.product_name} (SKU: ${i.sku})` : i.product_name)).join(", ")
        : (quoteOnTheGoNote.trim() ? `On-the-go: ${quoteOnTheGoNote.trim().slice(0, 80)}` : "Photo attached — see image");

      const totalQty = validItems.length > 0
        ? validItems.reduce((s, i) => s + (parseInt(i.quantity) || 1), 0)
        : 1;

      const res = await fetch(`${API}/api/order-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_type: "backorder",
          hospital_id: hospitalContext?.id || null,
          hospital_name: contact.hospital,
          address: shippingAddress,
          contact_name: contact.name,
          contact_email: contact.email,
          contact_phone: contact.phone || "",
          item_name: itemNameStr,
          mfr_number: validItems.length === 1 ? (validItems[0].sku || "") : "",
          quantity: totalQty,
          notes: supplierNotes,
          items: validItems.map((i) => ({
            name: i.product_name,
            sku: i.sku || "",
            quantity: parseInt(i.quantity) || 1,
            uom: i.uom || "case",
            notes: i.notes || "",
          })),
          shipping_address: shippingAddress,
          use_hospital_courier: freight.use_hospital_courier,
          hospital_courier_account: freight.use_hospital_courier ? freight.account : "",
          ship_via: freight.use_hospital_courier ? "Hospital 3rd-Party Courier" : "Best Way",
          terms: "Net 30",
          image_url: quoteImage?.url || null,
        }),
      });

      if (!res.ok) {
        toast.error("Failed to submit. Please try again or call 281.456.3458");
        return;
      }
      let respData = {};
      try { respData = await res.json(); } catch (_e) { /* non-json */ }
      // Save the backorder id so the success view can poll the discovery progress
      if (respData.backorder_id) {
        setBackorderId(respData.backorder_id);
      }
      if (onSubmitted) {
        onSubmitted(respData);
        return;
      }
      // Default: show inline success card (now with a Searching... spinner)
      setSubmitted(true);
      toast.success("Request submitted! Searching for stock…");
    } catch (_e) {
      toast.error("Network error. Please call us at 281.456.3458");
    } finally {
      setSubmitting(false);
    }
  };

  // Inline success view (no parent onSubmitted handler)
  if (submitted) {
    const isRunning = discovery.status !== "complete";
    const summary = discovery.summary;
    const headlineColor =
      summary === "found_verified" ? "border-emerald-300" :
      summary === "found_unverified" ? "border-cyan-300" :
      summary === "none" ? "border-amber-300" :
      "border-slate-300";
    const iconColor =
      summary === "found_verified" ? "text-emerald-500" :
      summary === "found_unverified" ? "text-cyan-500" :
      summary === "none" ? "text-amber-500" :
      "text-slate-400";

    return (
      <div className={`bg-white border ${headlineColor} rounded-xl p-8 text-center shadow-2xl shadow-slate-900/40`} data-testid="quote-success">
        {isRunning ? (
          <div className="w-14 h-14 mx-auto mb-4 rounded-full border-4 border-cyan-200 border-t-cyan-600 animate-spin" data-testid="discovery-spinner" />
        ) : (
          <CheckCircle2 className={`w-14 h-14 ${iconColor} mx-auto mb-4`} />
        )}
        <h3 className="text-xl font-bold text-slate-900 mb-2">
          {isRunning ? "Searching for stock…" :
            summary === "found_verified" ? "Possible stock found at a verified Sterile Mate supplier" :
            summary === "found_unverified" ? "Possible stock found via an unverified supplier" :
            summary === "none" ? "We are working on it" :
            "Request received"}
        </h3>
        <p className="text-slate-600 mb-2 max-w-md mx-auto">
          {discovery.message || "Our sourcing team will respond within 2 hours with pricing and availability."}
        </p>
        {!isRunning && discovery.lead_count > 0 && (
          <p className="text-xs text-slate-400 mb-4" data-testid="discovery-lead-count">
            {discovery.lead_count} possible {discovery.lead_count === 1 ? "source" : "sources"} identified · we'll contact them now and revert
          </p>
        )}
        {isRunning && (
          <p className="text-xs text-slate-400 mb-4">
            Scanning our verified supplier network and trusted external sources. This usually takes 30 to 60 seconds.
          </p>
        )}
        <p className="text-sm text-slate-500 mb-4">
          Confirmation sent to <strong className="text-slate-900">{contact.email}</strong>
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Button
            onClick={() => {
              setSubmitted(false);
              setBackorderId(null);
              setDiscovery({ status: "running", summary: null, message: "", lead_count: 0 });
              setQuoteItems([{ product_name: "", sku: "", quantity: "", uom: "case", notes: "" }]);
              setContact({ name: "", email: "", phone: "", hospital: "" });
              setQuoteImage(null);
              setQuoteOnTheGoNote("");
              setShowOnTheGo(false);
            }}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            Submit Another
          </Button>
          <a href="tel:2814563458">
            <Button className="bg-[#0056A8] hover:bg-[#003366] text-white">
              <Phone className="w-4 h-4 mr-2" />
              Call 281.456.3458
            </Button>
          </a>
        </div>
      </div>
    );
  }

  const wrapperClasses = showWrapper
    ? "bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5 shadow-2xl shadow-slate-900/40 ring-1 ring-amber-400/20"
    : "space-y-5";

  return (
    <form onSubmit={handleSubmit} className={wrapperClasses} data-testid="quick-quote-form">
      {hospitalContext && (
        <div className="-mt-1 mb-1 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="quick-quote-hospital-banner">
          <div className="flex items-center gap-2 text-xs text-emerald-800">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>
              Submitting as <strong className="font-bold">{hospitalContext.name || "your hospital"}</strong> — this request will appear in your dashboard.
            </span>
          </div>
          {backLink && (
            <a
              href="/hospital/portal?tab=backorders"
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
              data-testid="quick-quote-back-to-dashboard"
            >
              Back to dashboard →
            </a>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100 -mt-1">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-600">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          No account · No commitment · Confidential
        </div>
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          2-Hour Response Guarantee
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Your Name *</label>
          <Input
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            placeholder="John Smith"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400"
            required
            autoComplete="name"
            autoCapitalize="words"
            data-testid="qq-name"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Hospital / Facility *</label>
          <Input
            value={contact.hospital}
            onChange={(e) => setContact({ ...contact, hospital: e.target.value })}
            placeholder="Hospital or facility name"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400"
            required
            autoComplete="organization"
            autoCapitalize="words"
            data-testid="qq-hospital"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Email *</label>
          <Input
            type="email"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            placeholder="john@hospital.org"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400"
            required
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            data-testid="qq-email"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Phone</label>
          <Input
            type="tel"
            value={contact.phone}
            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
            placeholder="(281) 555-0123"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400"
            autoComplete="tel"
            inputMode="tel"
            data-testid="qq-phone"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-bold text-slate-900">Backordered or Hard-to-Find Items</label>
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 font-bold"
          >
            <Plus className="w-3.5 h-3.5" /> Add Another Product
          </button>
        </div>

        {/* "Order This Item Again" carousel — only when hospital is recognized */}
        {hospitalContext?.id && itemHistory.length > 0 && !quoteItems[0]?.product_name && (
          <div className="mb-3" data-testid="reorder-carousel">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1.5 flex items-center gap-1.5">
              <RefreshCw className="w-3 h-3" />
              Order This Item Again
              <span className="text-slate-400 font-medium normal-case tracking-normal ml-1">— click to autofill</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1.5 -mx-1 px-1">
              {itemHistory.slice(0, 8).map((it, idx) => (
                <button
                  type="button"
                  key={`hist-${it.name}-${idx}`}
                  onClick={() => applyHistoryItem(0, it)}
                  className="shrink-0 w-44 p-2.5 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 rounded-lg text-left transition-all"
                  data-testid={`reorder-card-${idx}`}
                >
                  <div className="text-xs font-bold text-emerald-900 truncate">{it.name}</div>
                  {it.part_number && <div className="text-[11px] text-emerald-700 font-mono truncate">#{it.part_number}</div>}
                  {it.manufacturer && <div className="text-[10px] text-emerald-700 truncate">{it.manufacturer}</div>}
                  <div className="text-[10px] text-slate-500 mt-1">
                    Last qty: {it.last_quantity || "—"} · ordered {it.times_ordered}×
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {quoteItems.map((item, idx) => (
            <div key={idx} className="space-y-2 rounded-lg border border-slate-200 bg-white sm:bg-transparent sm:border-0 p-3 sm:p-0" data-testid={`qq-item-${idx}`}>
              {/* Row 1 (mobile+desktop): # badge + Product name (full width on mobile) */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-slate-900 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-white font-bold">{idx + 1}</span>
                </div>
                <div className="relative flex-1 min-w-0">
                  <Input
                    value={item.product_name}
                    onChange={(e) => updateItem(idx, "product_name", e.target.value)}
                    onFocus={() => {
                      setActiveItemIdx(idx);
                      if (item.product_name.trim().length >= 2 && typeaheadResults.length > 0) {
                        setShowTypeahead(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowTypeahead(false), 180)}
                    placeholder="Product name / description"
                    className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base w-full focus-visible:ring-amber-500 focus-visible:border-amber-400"
                    data-testid={`qq-product-${idx}`}
                    autoComplete="off"
                  />
                  {hospitalContext?.id && showTypeahead && activeItemIdx === idx && typeaheadResults.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="qq-typeahead-results">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 px-3 pt-2 pb-1 font-bold">
                        From your order history
                      </div>
                      {typeaheadResults.map((hit, hidx) => (
                        <button
                          type="button"
                          key={`${hit.name}-${hit.part_number || hidx}`}
                          onMouseDown={(e) => { e.preventDefault(); applyHistoryItem(idx, hit); }}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0 border-slate-100 transition-colors"
                          data-testid={`qq-typeahead-row-${hidx}`}
                        >
                          <div className="text-sm font-semibold text-slate-800 truncate">{hit.name}</div>
                          <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                            {hit.part_number && <span className="font-mono">#{hit.part_number}</span>}
                            {hit.manufacturer && <span>· {hit.manufacturer}</span>}
                            {hit.times_ordered > 1 && <span className="text-emerald-700 inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> ordered {hit.times_ordered}×</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Remove button — always on Row 1, right-aligned */}
                {quoteItems.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="w-11 h-11 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                    aria-label={`Remove item ${idx + 1}`}
                    data-testid={`qq-remove-${idx}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
                )}
              </div>

              {/* Row 2 (mobile): SKU + Qty side-by-side. Desktop keeps them inline via sm:pl-9. */}
              <div className="flex items-center gap-2 sm:pl-9">
                <Input
                  value={item.sku}
                  onChange={(e) => updateItem(idx, "sku", e.target.value)}
                  placeholder="SKU / Part #"
                  className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base flex-1 sm:flex-none sm:w-40 focus-visible:ring-amber-500 focus-visible:border-amber-400"
                  autoComplete="off"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid={`qq-sku-${idx}`}
                />
                <Input
                  type="text"
                  value={item.quantity}
                  onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                  placeholder="Qty"
                  className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base w-20 focus-visible:ring-amber-500 focus-visible:border-amber-400"
                  inputMode="numeric"
                  autoComplete="off"
                  data-testid={`qq-qty-${idx}`}
                />
                <select
                  value={item.uom || "case"}
                  onChange={(e) => updateItem(idx, "uom", e.target.value)}
                  aria-label="Unit of measure"
                  className="bg-slate-50 border border-slate-300 text-slate-900 h-11 text-sm rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 font-semibold"
                  data-testid={`qq-uom-${idx}`}
                >
                  <option value="case">Case / Pack</option>
                  <option value="each">Eaches</option>
                  <option value="box">Box</option>
                  <option value="pallet">Pallet</option>
                </select>
              </div>

              {/* Row 3 (mobile+desktop): Notes — full-width on mobile, indented on desktop */}
              <div className="flex items-center gap-2 sm:pl-9">
                <Input
                  value={item.notes}
                  onChange={(e) => updateItem(idx, "notes", e.target.value)}
                  placeholder="Notes (optional — e.g. lot, color, size, urgency)"
                  className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-10 text-base flex-1 focus-visible:ring-amber-500 focus-visible:border-amber-400"
                />
              </div>
            </div>
          ))}
        </div>

        {/* On-the-go panel */}
        <div className="mt-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-3" data-testid="qq-on-the-go">
          <button
            type="button"
            onClick={() => setShowOnTheGo((v) => !v)}
            className="w-full flex items-center justify-between text-xs font-bold text-amber-800"
          >
            <span className="inline-flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" />
              On the go? Paste a photo or drop a quick note
            </span>
            <span className="text-amber-700">{showOnTheGo ? "Hide" : "Open"}</span>
          </button>
          {showOnTheGo && (
            <div className="mt-3 space-y-2">
              <div
                onPaste={onPasteImage}
                className="rounded border border-amber-200 bg-white p-3 text-xs text-slate-700 cursor-text"
                tabIndex={0}
                data-testid="qq-image-paste-zone"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-500">
                    Click here then <strong>paste</strong> (Ctrl/⌘+V) a screenshot, or use the button:
                  </span>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 hover:underline">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => uploadImage(e.target.files?.[0])}
                      className="hidden"
                      data-testid="qq-image-file-input"
                    />
                    <Camera className="w-3.5 h-3.5" /> Snap / Choose photo
                  </label>
                </div>
                {quoteImageUploading && <p className="text-[11px] text-amber-700 mt-2">Uploading…</p>}
                {quoteImage?.preview && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={quoteImage.preview} alt="attached" className="h-20 rounded border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => setQuoteImage(null)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <textarea
                rows={2}
                value={quoteOnTheGoNote}
                onChange={(e) => setQuoteOnTheGoNote(e.target.value)}
                placeholder='Quick note — e.g. "OR running low on Bovie pencils 6", need 24 by Friday, please RSVP.'
                className="w-full text-sm bg-white border border-amber-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
                data-testid="qq-on-the-go-note"
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 pt-3 border-t border-slate-100">
        <div className="col-span-12 mb-1">
          <label className="text-sm font-bold text-slate-900 flex items-center gap-2">
            Ship-To{" "}
            <span className="text-xs font-normal text-slate-500">
              — city / state / zip only; we keep your facility name off supplier RFQs
            </span>
          </label>
        </div>
        <div className="col-span-12 sm:col-span-5">
          <Input
            value={shipping.city}
            onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
            placeholder="City"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400"
            autoComplete="address-level2"
            autoCapitalize="words"
            data-testid="qq-ship-city"
          />
        </div>
        <div className="col-span-4 sm:col-span-2">
          <Input
            value={shipping.state}
            onChange={(e) => setShipping({ ...shipping, state: e.target.value.toUpperCase().slice(0, 2) })}
            placeholder="ST"
            maxLength={2}
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400 uppercase"
            autoComplete="address-level1"
            autoCapitalize="characters"
            data-testid="qq-ship-state"
          />
        </div>
        <div className="col-span-8 sm:col-span-5">
          <Input
            value={shipping.zip}
            onChange={(e) => setShipping({ ...shipping, zip: e.target.value })}
            placeholder="ZIP code"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base focus-visible:ring-amber-500 focus-visible:border-amber-400"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            data-testid="qq-ship-zip"
          />
        </div>
      </div>

      <div className="pt-3 border-t border-slate-100">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={freight.use_hospital_courier}
            onChange={(e) => setFreight({ ...freight, use_hospital_courier: e.target.checked })}
            className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer flex-shrink-0"
            data-testid="qq-use-hospital-courier"
          />
          <span className="text-sm text-slate-800 leading-snug">
            <strong className="text-slate-900">Use hospital 3rd-party courier for freight charges</strong>
            <span className="block text-xs text-slate-600 mt-0.5">
              Check this if your facility ships on its own FedEx / UPS / freight account. Otherwise suppliers will add freight to their quote.
            </span>
          </span>
        </label>
        {freight.use_hospital_courier && (
          <Input
            value={freight.account}
            onChange={(e) => setFreight({ ...freight, account: e.target.value })}
            placeholder="Courier & account # (e.g. FedEx 1234-5678-9)"
            className="bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 h-11 text-base mt-2 focus-visible:ring-amber-500 focus-visible:border-amber-400"
            autoComplete="off"
            data-testid="qq-courier-account"
          />
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <p className="text-xs text-slate-500">
          Don&apos;t see your item? <strong className="text-slate-700">List it anyway</strong> — if it exists, we&apos;ll find it.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 px-8 h-11 font-bold shadow-lg shadow-amber-500/20"
          data-testid="qq-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Get Quote in 2 Hours
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
